import React, { useState, useEffect, useMemo } from 'react';
import usePersistedState from '../hooks/usePersistedState';
import { useQuery, keepPreviousData, useMutation, useQueryClient } from '@tanstack/react-query';
import { getCopiedWallets, getArmingEntities, putArmingEntity, deleteArmingEntity } from '../services/api';
import NavRail from '../components/NavRail';
import TokenDrillPanel from '../components/TokenDrillPanel';
import CopyBtn from '../components/CopyBtn';
import CopytradeDiscovery from '../components/CopytradeDiscovery';

/* ============================================================
   WALLETS COPIÉS — Backoffice bot memecoin Solana
   Source : copytrade_wallets (PG) + stream Redis copytrade:would_buy
   Colonnes wallets : wallet, win_rate, sol/j, tier, is_active, tokens shadow
   Colonnes trades shadow : wallet, mint, bc_pda, sol, prix, ts_observed
   ============================================================ */

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;500;600;700;800&display=swap');
:root{--bg:#0a0b0e;--surface:#14161c;--surface2:#191c23;--border:#1f2329;--text:#e6e8ec;--text2:#8a8f98;
  --green:#00e08a;--red:#ff3b5c;--amber:#ffb020;--cyan:#22d3ee;}
.cw *{box-sizing:border-box;margin:0;padding:0}
.cw,.cw *{font-family:'JetBrains Mono',ui-monospace,monospace;}
.cw{background:var(--bg);color:var(--text);height:100vh;display:flex;overflow:hidden;font-size:13px;line-height:1.35;-webkit-font-smoothing:antialiased;}
.cw .sb{width:64px;flex:0 0 64px;border-right:1px solid var(--border);background:#0c0d11;display:flex;flex-direction:column;align-items:center;padding:14px 0;gap:6px;}
.cw .sb-logo{width:34px;height:34px;border-radius:8px;background:var(--red);color:#fff;display:flex;align-items:center;justify-content:center;font-weight:800;font-size:16px;margin-bottom:14px;box-shadow:0 0 14px rgba(255,59,92,.45);}
.cw .sb-item{width:40px;height:40px;border-radius:8px;display:flex;align-items:center;justify-content:center;color:var(--text2);cursor:pointer;position:relative;transition:all .12s;font-size:16px;border:none;background:transparent;}
.cw .sb-item:hover{background:var(--surface);color:var(--text);}
.cw .sb-item.on{background:var(--surface);color:var(--cyan);box-shadow:inset 2px 0 0 var(--cyan);}
.cw .sb-item .tip{position:absolute;left:52px;top:50%;transform:translateY(-50%);background:#000;border:1px solid var(--border);padding:4px 9px;border-radius:6px;font-size:11px;white-space:nowrap;opacity:0;pointer-events:none;transition:opacity .12s;z-index:50;color:var(--text);}
.cw .sb-item:hover .tip{opacity:1;}
.cw .sb-spacer{flex:1;}
.cw .main{flex:1;display:flex;flex-direction:column;overflow:hidden;}
.cw .top{display:flex;align-items:center;gap:18px;padding:11px 18px;border-bottom:1px solid var(--border);background:#0c0d11;flex:0 0 auto;}
.cw .top-title{font-size:14px;font-weight:800;letter-spacing:.04em;color:var(--text);}
.cw .top-spacer{flex:1;}
.cw .clock{font-size:11px;color:var(--text2);text-align:right;}
.cw .clock b{color:var(--text);font-weight:600;}
.cw .page{flex:1;overflow:auto;padding:16px;}
.cw .strip{display:grid;grid-template-columns:repeat(4,1fr);gap:10px;margin-bottom:16px;}
@media(max-width:900px){.cw .strip{grid-template-columns:repeat(2,1fr);}}
.cw .kpi{background:var(--surface);border:1px solid var(--border);border-radius:10px;padding:11px 13px;}
.cw .kpi .k{font-size:9.5px;color:var(--text2);letter-spacing:.07em;text-transform:uppercase;}
.cw .kpi .v{font-size:20px;font-weight:800;margin-top:4px;}
.cw .kpi .v.cy{color:var(--cyan);}
.cw .kpi .v.gr{color:var(--green);}
.cw .kpi .v.am{color:var(--amber);}
.cw .bar-row{display:flex;align-items:center;justify-content:space-between;margin-bottom:12px;gap:12px;flex-wrap:wrap;}
.cw .h1{font-size:13px;letter-spacing:.16em;text-transform:uppercase;color:var(--text2);font-weight:600;}
.cw .sort-row{display:flex;align-items:flex-end;gap:12px;flex-wrap:wrap;}
.cw .fgrp{display:flex;align-items:center;gap:6px;}
.cw .sort-lbl{font-size:9.5px;letter-spacing:.1em;text-transform:uppercase;color:var(--text2);font-weight:600;}
.cw .sort-btn{background:none;border:1px solid var(--border);color:var(--text2);font-family:inherit;font-size:10px;font-weight:600;padding:5px 11px;border-radius:6px;cursor:pointer;letter-spacing:.04em;transition:all .12s;}
.cw .sort-btn.on{background:var(--surface2);color:var(--cyan);border-color:var(--cyan);}
.cw .sort-btn:hover{color:var(--text);}
.cw .grid2{display:grid;grid-template-columns:1fr 1.5fr;gap:12px;}
@media(max-width:1100px){.cw .grid2{grid-template-columns:1fr;}}
.cw .tbl-wrap{background:var(--surface);border:1px solid var(--border);border-radius:12px;overflow:hidden;margin-bottom:12px;}
.cw .tbl-hd{padding:10px 14px;border-bottom:1px solid var(--border);font-size:11px;letter-spacing:.14em;text-transform:uppercase;color:var(--text2);font-weight:600;display:flex;align-items:center;justify-content:space-between;}
.cw .tbl-hd .cnt{font-size:10px;color:var(--text2);letter-spacing:0;text-transform:none;}
.cw table{width:100%;border-collapse:collapse;}
.cw thead th{font-size:9px;letter-spacing:.09em;text-transform:uppercase;color:var(--text2);text-align:left;padding:8px 12px;font-weight:600;background:#0e1014;}
.cw thead th.num{text-align:right;}
.cw tbody td{padding:9px 12px;font-size:11.5px;border-top:1px solid #16191f;vertical-align:middle;}
.cw tbody td.num{text-align:right;font-variant-numeric:tabular-nums;}
.cw tbody tr:hover{background:var(--surface2);}
.cw .addr{font-size:10px;color:var(--text2);font-family:'JetBrains Mono',monospace;}
.cw .badge-tier{font-size:9.5px;font-weight:700;padding:2px 8px;border-radius:5px;display:inline-block;}
.cw .badge-tier.elite{background:rgba(255,176,32,.18);color:var(--amber);}
.cw .badge-tier.standard{background:rgba(34,211,238,.12);color:var(--cyan);}
.cw .badge-tier.inactive{background:rgba(138,143,152,.13);color:var(--text2);}
.cw .pos{color:var(--green);}
.cw .neg{color:var(--red);}
.cw .muted{color:var(--text2);}
.cw .shadow-note{font-size:10px;color:var(--text2);padding:6px 14px;border-bottom:1px solid var(--border);background:rgba(0,0,0,.3);}
.cw .err-banner{font-size:11px;color:var(--amber);margin-bottom:12px;padding:8px 12px;border-radius:8px;background:rgba(255,176,32,.10);border:1px solid rgba(255,176,32,.35);}
.cw .loading{color:var(--text2);padding:24px;font-size:12.5px;}
.cw .nodata{display:flex;align-items:center;justify-content:center;min-height:80px;font-size:11.5px;color:var(--text2);border:1px dashed var(--border);border-radius:8px;}
.cw .cs-tabs{display:flex;gap:6px;margin-bottom:16px;}
.cw .tdp-btn{background:none;border:1px solid var(--border);color:var(--text2);font-family:inherit;font-size:10px;font-weight:600;padding:5px 10px;border-radius:6px;cursor:pointer;letter-spacing:.03em;}
.cw .tdp-btn.on{background:var(--surface2);color:var(--cyan);border-color:var(--cyan);}
.cw .cs-wrap{margin-top:6px;}
.cw .cs-err{color:var(--red);font-size:12px;margin-bottom:8px;}
.cw .cs-slot{color:var(--text2);font-size:10px;}
.cw .cs-badge{font-size:9px;font-weight:700;padding:2px 6px;border-radius:5px;text-transform:uppercase;}
.cw .cs-badge[data-ev="live"]{background:rgba(0,224,138,.16);color:var(--green);}
.cw .cs-badge[data-ev="batch"]{background:rgba(255,176,32,.16);color:var(--amber);}
.cw table.disc-tbl{width:100%;border-collapse:collapse;background:var(--surface);border:1px solid var(--border);border-radius:10px;overflow:hidden;}
.cw table.disc-tbl th{font-size:9px;letter-spacing:.08em;text-transform:uppercase;color:var(--text2);text-align:left;padding:8px 10px;background:#0e1014;font-weight:600;}
.cw table.disc-tbl td{padding:8px 10px;font-size:11.5px;border-top:1px solid #16191f;vertical-align:middle;}
.cw .arm-bar{display:flex;align-items:center;gap:8px;flex-wrap:wrap;margin-bottom:10px;}
.cw .arm-in{background:var(--surface2);color:var(--text);border:1px solid var(--border);border-radius:6px;padding:6px 10px;font-family:inherit;font-size:12px;}
.cw .act-btn{background:none;border:1px solid var(--border);color:var(--text2);font-family:inherit;font-size:9.5px;font-weight:700;padding:3px 8px;border-radius:5px;cursor:pointer;transition:all .12s;letter-spacing:.02em;}
.cw .act-btn:hover{color:var(--text);border-color:var(--text2);}
.cw .act-btn.obs:hover{color:var(--cyan);border-color:var(--cyan);}
.cw .act-btn.arm:hover{color:var(--green);border-color:var(--green);}
.cw .act-btn.rej:hover{color:var(--red);border-color:var(--red);}
.cw .act-btn:disabled{opacity:.4;cursor:default;}
.cw .popover{position:absolute;z-index:100;background:#0e1014;border:1px solid var(--border);border-radius:8px;padding:10px 12px;display:flex;flex-direction:column;gap:7px;min-width:220px;box-shadow:0 6px 24px rgba(0,0,0,.55);}
.cw .popover-row{display:flex;align-items:center;gap:7px;}
.cw .pop-lbl{font-size:9px;color:var(--text2);letter-spacing:.06em;text-transform:uppercase;min-width:70px;}
.cw .pop-in{background:none;border:1px solid var(--border);color:var(--text);font-family:inherit;font-size:10px;font-weight:600;padding:4px 7px;border-radius:5px;outline:none;width:80px;}
.cw .pop-in:focus{border-color:var(--green);}
.cw .arm-chip{font-size:9px;font-weight:800;padding:2px 7px;border-radius:5px;margin-left:6px;letter-spacing:.04em;white-space:nowrap;}
.cw .arm-chip.buy{background:rgba(0,224,138,.18);color:var(--green);box-shadow:0 0 8px rgba(0,224,138,.25);}
.cw .arm-chip.obs{background:rgba(34,211,238,.14);color:var(--cyan);}
`;

// Mapping en-tête → clé sort backend (null = non triable)
const W_COL_SORT = {
  wallet: 'wallet',
  win_rate: 'win_rate',
  net_sol: 'net_sol',
  sol_per_day: 'sol_per_day',
  tokens_per_day: 'tokens_per_day',
  n_tokens: 'n_tokens',
  peak_median: 'peak_median_pct',
  rug: 'rug_pct',
  tier: 'tier',
  shadow: null,
  statut: null,
};

function fmt(v, decimals = 2) {
  if (v === null || v === undefined) return <span style={{ color: 'var(--text2)' }}>—</span>;
  return v.toFixed(decimals);
}

function fmtAddr(addr) {
  if (!addr) return '—';
  return `${addr.slice(0, 6)}…${addr.slice(-4)}`;
}

function fmtTs(iso) {
  if (!iso) return <span style={{ color: 'var(--text2)' }}>—</span>;
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  } catch {
    return iso;
  }
}

function CopiedWalletsPage() {
  const [view, setView] = useState('wallets'); // 'wallets' | 'discovery'
  const [sort, setSort] = usePersistedState('wallets.sort', 'sol_per_day');
  const [sortDir, setSortDir] = usePersistedState('wallets.sortDir', 'desc');
  const [minPeakMedian, setMinPeakMedian] = usePersistedState('wallets.minPeakMedian', null);
  const [minWr, setMinWr] = usePersistedState('wallets.minWr', null);
  const [maxRug, setMaxRug] = usePersistedState('wallets.maxRug', null);
  const [activeOnly, setActiveOnly] = usePersistedState('wallets.activeOnly', false);
  const [migrated, setMigrated] = usePersistedState('wallets.migrated', null);
  const [almostBonded, setAlmostBonded] = usePersistedState('wallets.almostBonded', false);
  const [minTokens, setMinTokens] = usePersistedState('wallets.minTokens', null);
  const [armState, setArmState] = usePersistedState('wallets.armState', null); // null=Tous | 'armed' | 'observe' | 'none'
  const [search, setSearch] = useState('');
  const [drillWallet, setDrillWallet] = useState(null);
  const [drillBacktest, setDrillBacktest] = useState(false);
  const [clock, setClock] = useState(new Date());

  const qc = useQueryClient();
  const [actionErr, setActionErr] = useState(null);
  const [armPopover, setArmPopover] = useState({});
  const openArm = (a) => setArmPopover((s) => ({ ...s, [a]: { open: true, size_mult: '', size_cap_sol: '' } }));
  const closeArm = (a) => setArmPopover((s) => ({ ...s, [a]: { ...s[a], open: false } }));
  const patchArm = (a, p) => setArmPopover((s) => ({ ...s, [a]: { ...s[a], ...p } }));

  const armQ = useQuery({ queryKey: ['arming-entities'], queryFn: getArmingEntities, refetchInterval: 30000 });
  const armedByAddr = useMemo(() => {
    const m = {};
    (armQ.data?.entities || []).forEach((e) => { if (e.kind === 'wallet' && e.active) m[e.address] = e; });
    return m;
  }, [armQ.data]);

  const invalidate = () => { qc.invalidateQueries({ queryKey: ['arming-entities'] }); qc.invalidateQueries({ queryKey: ['copied-wallets'] }); };
  const mArm = useMutation({
    // tip_lamports forwarded si présent dans le payload (préserve l'override sur re-arm).
    // Ces boutons n'apparaissent que pour les wallets non-armés (armedByAddr[w] = undefined),
    // donc tip_lamports est undefined → NULL en pratique. Commentaire ici pour leçon M-1 :
    // ne pas omettre tip_lamports dans un PUT sur entité existante.
    mutationFn: ({ wallet, mode, size_mult, size_cap_sol, tip_lamports }) => putArmingEntity({ kind: 'wallet', address: wallet, mode, size_mult, size_cap_sol, tip_lamports }),
    onSuccess: () => { setActionErr(null); invalidate(); },
    onError: (e) => setActionErr(e?.response?.data?.detail || e?.message || 'Erreur inconnue'),
  });
  const mDisarm = useMutation({
    mutationFn: (id) => deleteArmingEntity(id),
    onSuccess: () => { setActionErr(null); invalidate(); },
    onError: (e) => setActionErr(e?.response?.data?.detail || e?.message || 'Erreur inconnue'),
  });
  const doArm = (wallet) => {
    const st = armPopover[wallet] || {};
    const body = { wallet, mode: 'buy' };
    if (st.size_mult !== '' && st.size_mult != null) body.size_mult = Number(st.size_mult);
    if (st.size_cap_sol !== '' && st.size_cap_sol != null) body.size_cap_sol = Number(st.size_cap_sol);
    // Forwarde tip_lamports depuis l'entité existante si disponible (leçon M-1 : évite clobber).
    // En pratique armedByAddr[wallet] est undefined ici (bouton "Armer" masqué si déjà armé).
    const existing = armedByAddr[wallet];
    if (existing?.tip_lamports) body.tip_lamports = existing.tip_lamports;
    closeArm(wallet); mArm.mutate(body);
  };

  useEffect(() => {
    const i = setInterval(() => setClock(new Date()), 1000);
    return () => clearInterval(i);
  }, []);

  const onSort = (colKey) => {
    const backendCol = W_COL_SORT[colKey];
    if (!backendCol) return;
    if (sort === backendCol) {
      setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    } else {
      setSort(backendCol);
      setSortDir('desc');
    }
  };

  const sortIndicator = (colKey) => {
    const backendCol = W_COL_SORT[colKey];
    if (!backendCol || sort !== backendCol) return null;
    return <span style={{ marginLeft: 3 }}>{sortDir === 'desc' ? '▼' : '▲'}</span>;
  };

  const thStyle = (colKey) => ({
    cursor: W_COL_SORT[colKey] ? 'pointer' : 'default',
    userSelect: 'none',
  });

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['copied-wallets', sort, sortDir, minPeakMedian, minWr, maxRug, activeOnly, migrated, almostBonded, minTokens],
    queryFn: () => getCopiedWallets({ sort, sortDir, minPeakMedian, minWr, maxRug, activeOnly, migrated, almostBonded, minTokens, limit: 100, skip: 0 }),
    refetchInterval: 60000,
    placeholderData: keepPreviousData,
  });

  const wallets = data?.wallets || [];

  const filteredWallets = useMemo(() => {
    const q = search.trim().toLowerCase();
    let out = wallets;
    if (q) out = out.filter((w) => (w.wallet || '').toLowerCase().includes(q));
    if (armState) {
      out = out.filter((w) => {
        const e = armedByAddr[w.wallet];
        if (armState === 'armed') return e?.mode === 'buy';
        if (armState === 'observe') return e?.mode === 'observe';
        if (armState === 'none') return !e;
        return true;
      });
    }
    return out;
  }, [wallets, search, armState, armedByAddr]);

  const total = data?.total || 0;
  const activeCount = data?.active_count || 0;
  const shadowTrades = data?.shadow_trades || [];
  const shadowTotal = data?.shadow_total || 0;
  const tradesByWallet = data?.trades_by_wallet || {};
  const shadowError = data?.shadow_error || null;

  const t = clock.toLocaleTimeString('fr-FR', { hour12: false });

  const rail = <NavRail />;

  return (
    <div className="cw">
      <style>{CSS}</style>
      {rail}
      <div className="main">
        <div className="top">
          <span className="top-title">Wallets copiés</span>
          <div className="top-spacer" />
          <div className="clock"><b>{t}</b></div>
        </div>
        <div className="page">
          <div className="cs-tabs">
            <button className={`tdp-btn ${view === 'wallets' ? 'on' : ''}`} onClick={() => setView('wallets')}>Wallets copiés</button>
            <button className={`tdp-btn ${view === 'discovery' ? 'on' : ''}`} onClick={() => setView('discovery')}>Découverte</button>
          </div>

          {view === 'discovery' && <CopytradeDiscovery />}

          {view === 'wallets' && <>
          {/* KPIs */}
          <div className="strip">
            <div className="kpi">
              <div className="k">Wallets profil.</div>
              <div className="v cy">{total}</div>
            </div>
            <div className="kpi">
              <div className="k">Actifs (Redis)</div>
              <div className="v gr">{activeCount}</div>
            </div>
            <div className="kpi">
              <div className="k">Shadow trades</div>
              <div className="v am">{shadowTotal}</div>
            </div>
            <div className="kpi">
              <div className="k">Mode</div>
              <div className="v" style={{ fontSize: 13, color: 'var(--text2)', marginTop: 6 }}>SHADOW ONLY</div>
            </div>
          </div>

          {isError && (
            <div className="err-banner">Erreur chargement : {error?.message || 'inconnue'}</div>
          )}
          {shadowError && (
            <div className="err-banner">Redis copytrade:would_buy : {shadowError}</div>
          )}
          {actionErr && <div className="err-banner">Erreur action : {actionErr}</div>}

          {/* Wallets table */}
          <div className="bar-row">
            <span className="h1">Wallets ({filteredWallets.length})</span>
            <div className="sort-row">
              {/* 0. Recherche adresse */}
              <div className="fgrp">
                <input
                  type="text"
                  placeholder="🔍 Adresse…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{ width: 200, background: 'none', border: '1px solid var(--border)', color: 'var(--text)', fontFamily: 'inherit', fontSize: 11, fontWeight: 600, padding: '6px 9px', borderRadius: 6, outline: 'none' }}
                />
                {search && <button className="sort-btn" onClick={() => setSearch('')} title="effacer">✕</button>}
              </div>

              {/* 1. toggle */}
              <div className="fgrp">
                <button
                  className={`sort-btn ${activeOnly ? 'on' : ''}`}
                  onClick={() => setActiveOnly(v => !v)}
                >Actifs seulement</button>
              </div>

              {/* 2. Peak médian */}
              <div className="fgrp">
                <span className="sort-lbl">Peak médian</span>
                <button
                  className={`sort-btn ${minPeakMedian === null ? 'on' : ''}`}
                  onClick={() => setMinPeakMedian(null)}
                >Tous</button>
                <button
                  className={`sort-btn ${minPeakMedian === 50 ? 'on' : ''}`}
                  onClick={() => setMinPeakMedian(50)}
                >≥+50%</button>
                <button
                  className={`sort-btn ${minPeakMedian === 100 ? 'on' : ''}`}
                  onClick={() => setMinPeakMedian(100)}
                >≥+100%</button>
                <input
                  type="number"
                  placeholder="%"
                  value={minPeakMedian ?? ''}
                  onChange={(e) => {
                    const val = e.target.value;
                    setMinPeakMedian(val === '' ? null : Number(val));
                  }}
                  style={{
                    width: 72,
                    background: 'none',
                    border: '1px solid var(--border)',
                    color: 'var(--text)',
                    fontFamily: 'inherit',
                    fontSize: 10,
                    fontWeight: 600,
                    padding: '5px 8px',
                    borderRadius: 6,
                    outline: 'none',
                  }}
                />
              </div>

              {/* 3. WR */}
              <div className="fgrp">
                <span className="sort-lbl">WR</span>
                <button
                  className={`sort-btn ${minWr === null ? 'on' : ''}`}
                  onClick={() => setMinWr(null)}
                >Tous</button>
                <button
                  className={`sort-btn ${minWr === 50 ? 'on' : ''}`}
                  onClick={() => setMinWr(50)}
                >≥50%</button>
                <button
                  className={`sort-btn ${minWr === 70 ? 'on' : ''}`}
                  onClick={() => setMinWr(70)}
                >≥70%</button>
                <input
                  type="number"
                  placeholder="WR ≥ %"
                  value={minWr ?? ''}
                  onChange={(e) => {
                    const val = e.target.value;
                    setMinWr(val === '' ? null : Number(val));
                  }}
                  style={{
                    width: 80,
                    background: 'none',
                    border: '1px solid var(--border)',
                    color: 'var(--text)',
                    fontFamily: 'inherit',
                    fontSize: 10,
                    fontWeight: 600,
                    padding: '5px 8px',
                    borderRadius: 6,
                    outline: 'none',
                  }}
                />
              </div>

              {/* 4. Rug max */}
              <div className="fgrp">
                <span className="sort-lbl">Rug max</span>
                <button
                  className={`sort-btn ${maxRug === null ? 'on' : ''}`}
                  onClick={() => setMaxRug(null)}
                >Tous</button>
                <button
                  className={`sort-btn ${maxRug === 10 ? 'on' : ''}`}
                  onClick={() => setMaxRug(10)}
                >≤10%</button>
                <button
                  className={`sort-btn ${maxRug === 25 ? 'on' : ''}`}
                  onClick={() => setMaxRug(25)}
                >≤25%</button>
                <input
                  type="number"
                  placeholder="rug ≤ %"
                  value={maxRug ?? ''}
                  onChange={(e) => {
                    const val = e.target.value;
                    setMaxRug(val === '' ? null : Number(val));
                  }}
                  style={{
                    width: 80,
                    background: 'none',
                    border: '1px solid var(--border)',
                    color: 'var(--text)',
                    fontFamily: 'inherit',
                    fontSize: 10,
                    fontWeight: 600,
                    padding: '5px 8px',
                    borderRadius: 6,
                    outline: 'none',
                  }}
                />
              </div>

              {/* 5. Nb tokens */}
              <div className="fgrp">
                <span className="sort-lbl">Nb tokens</span>
                <button
                  className={`sort-btn ${minTokens === null ? 'on' : ''}`}
                  onClick={() => setMinTokens(null)}
                >Tous</button>
                <button
                  className={`sort-btn ${minTokens === 100 ? 'on' : ''}`}
                  onClick={() => setMinTokens(100)}
                >≥100</button>
                <button
                  className={`sort-btn ${minTokens === 500 ? 'on' : ''}`}
                  onClick={() => setMinTokens(500)}
                >≥500</button>
                <input
                  type="number"
                  placeholder="tokens ≥"
                  value={minTokens ?? ''}
                  onChange={(e) => {
                    const val = e.target.value;
                    setMinTokens(val === '' ? null : Number(val));
                  }}
                  style={{
                    width: 88,
                    background: 'none',
                    border: '1px solid var(--border)',
                    color: 'var(--text)',
                    fontFamily: 'inherit',
                    fontSize: 10,
                    fontWeight: 600,
                    padding: '5px 8px',
                    borderRadius: 6,
                    outline: 'none',
                  }}
                />
              </div>

              {/* ligne 2 — Migré + Almost bonded sur une nouvelle ligne */}
              <div style={{ flexBasis: '100%', height: 0 }} />

              {/* 6. Migré */}
              <div className="fgrp">
                <span className="sort-lbl">Migré</span>
                <button
                  className={`sort-btn ${migrated === null ? 'on' : ''}`}
                  onClick={() => setMigrated(null)}
                >Tous</button>
                <button
                  className={`sort-btn ${migrated === 'true' ? 'on' : ''}`}
                  onClick={() => setMigrated('true')}
                >A migré</button>
                <button
                  className={`sort-btn ${migrated === 'false' ? 'on' : ''}`}
                  onClick={() => setMigrated('false')}
                >Jamais</button>
              </div>

              {/* extra : Almost bonded (hors ordre imposé, conservé) */}
              <div className="fgrp">
                <button
                  className={`sort-btn ${almostBonded ? 'on' : ''}`}
                  onClick={() => setAlmostBonded(v => !v)}
                >Almost bonded</button>
              </div>

              {/* 7. Armement (lu depuis /api/arming/entities = tracked_entities, client-side) */}
              <div className="fgrp">
                <span className="sort-lbl">Armement</span>
                <button
                  className={`sort-btn ${armState === null ? 'on' : ''}`}
                  onClick={() => setArmState(null)}
                >Tous</button>
                <button
                  className={`sort-btn ${armState === 'armed' ? 'on' : ''}`}
                  onClick={() => setArmState('armed')}
                >⚡ Armé</button>
                <button
                  className={`sort-btn ${armState === 'observe' ? 'on' : ''}`}
                  onClick={() => setArmState('observe')}
                >👁 Observé</button>
                <button
                  className={`sort-btn ${armState === 'none' ? 'on' : ''}`}
                  onClick={() => setArmState('none')}
                >Non suivi</button>
              </div>
            </div>
          </div>

          {isLoading ? (
            <div className="loading">Chargement...</div>
          ) : (
            <>
              <div className="tbl-wrap" style={{ marginBottom: 16 }}>
                {filteredWallets.length === 0 ? (
                  <div className="nodata" style={{ padding: 24 }}>
                    Aucun wallet — lancer populate_copytrade_wallets.py
                  </div>
                ) : (
                  <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                  <table>
                    <thead>
                      <tr>
                        <th style={thStyle('wallet')} onClick={() => onSort('wallet')}>Wallet{sortIndicator('wallet')}</th>
                        <th className="num" style={thStyle('win_rate')} onClick={() => onSort('win_rate')}>Win rate{sortIndicator('win_rate')}</th>
                        <th className="num" style={thStyle('net_sol')} onClick={() => onSort('net_sol')}>Net SOL{sortIndicator('net_sol')}</th>
                        <th className="num" style={thStyle('sol_per_day')} onClick={() => onSort('sol_per_day')}>SOL/j{sortIndicator('sol_per_day')}</th>
                        <th className="num" style={thStyle('tokens_per_day')} onClick={() => onSort('tokens_per_day')}>Tokens/j{sortIndicator('tokens_per_day')}</th>
                        <th className="num" style={thStyle('n_tokens')} onClick={() => onSort('n_tokens')}>N tokens{sortIndicator('n_tokens')}</th>
                        <th className="num" style={thStyle('peak_median')} onClick={() => onSort('peak_median')}>Peak médian{sortIndicator('peak_median')}</th>
                        <th className="num" style={thStyle('rug')} onClick={() => onSort('rug')}>Rug%{sortIndicator('rug')}</th>
                        <th style={thStyle('tier')} onClick={() => onSort('tier')}>Tier{sortIndicator('tier')}</th>
                        <th className="num" style={thStyle('shadow')}>Trades shadow</th>
                        <th style={thStyle('statut')}>Statut</th>
                        <th>Armement</th>
                        <th>Backtest</th>
                        <th>Liens</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredWallets.map(w => {
                        const tierCls = !w.is_active ? 'inactive' : w.tier === 'elite' ? 'elite' : 'standard';
                        return (
                          <tr key={w.wallet} style={{ cursor: 'pointer' }} onClick={() => { setDrillBacktest(false); setDrillWallet(w.wallet); }}>
                            <td>
                              <span
                                className="addr"
                                title={w.wallet}
                                style={{ cursor: 'pointer' }}
                                onClick={(e) => { e.stopPropagation(); navigator.clipboard?.writeText(w.wallet); }}
                              >{fmtAddr(w.wallet)}</span>
                              <CopyBtn text={w.wallet} />
                              {(() => {
                                const e = armedByAddr[w.wallet];
                                if (!e) return null;
                                if (e.mode === 'buy') return <span className="arm-chip buy" title={`armé ×${e.size_mult ?? '?'} cap ${e.size_cap_sol ?? '∞'} SOL`}>⚡ ARMÉ{e.size_mult != null ? ` ×${e.size_mult}` : ''}</span>;
                                return <span className="arm-chip obs" title="observé">👁 OBS</span>;
                              })()}
                            </td>
                            <td className="num">
                              {w.win_rate !== null
                                ? <span className={w.win_rate >= 50 ? 'pos' : 'muted'}>{w.win_rate.toFixed(1)}%</span>
                                : <span className="muted">—</span>}
                            </td>
                            <td className="num">
                              {w.net_sol !== null
                                ? <span className={w.net_sol > 0 ? 'pos' : 'neg'}>{w.net_sol > 0 ? '+' : ''}{w.net_sol.toFixed(2)}</span>
                                : <span className="muted">—</span>}
                            </td>
                            <td className="num">{fmt(w.sol_per_day)}</td>
                            <td className="num">{fmt(w.tokens_per_day, 1)}</td>
                            <td className="num">{w.n_tokens}</td>
                            <td className="num">
                              {w.peak_median_pct != null
                                ? <span className="pos">+{w.peak_median_pct.toFixed(1)}%{w.n_analyzed != null ? <span className="muted" style={{ fontSize: 9, marginLeft: 4 }}>n={w.n_analyzed}</span> : null}</span>
                                : <span className="muted">—</span>}
                            </td>
                            <td className="num">
                              {w.rug_pct != null
                                ? <span className={w.rug_pct <= 10 ? 'pos' : w.rug_pct >= 25 ? 'neg' : 'muted'}>{w.rug_pct.toFixed(1)}%</span>
                                : <span className="muted">—</span>}
                            </td>
                            <td><span className={`badge-tier ${tierCls}`}>{w.is_active ? w.tier : 'inactif'}</span></td>
                            <td className="num">{tradesByWallet[w.wallet] ?? <span className="muted">0</span>}</td>
                            <td>
                              <span style={{ fontSize: 9.5, color: w.is_active ? 'var(--green)' : 'var(--text2)', fontWeight: 700 }}>
                                {w.is_active ? 'actif' : 'inactif'}
                              </span>
                            </td>
                            <td onClick={(e) => e.stopPropagation()} style={{ position: 'relative', whiteSpace: 'nowrap' }}>
                              {(() => {
                                const armed = armedByAddr[w.wallet];
                                const busy = mArm.isPending || mDisarm.isPending;
                                if (armed) {
                                  return (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                      {armed.size_mult != null && <span style={{ fontSize: 9, color: 'var(--green)', fontWeight: 700 }}>×{armed.size_mult}</span>}
                                      <button className="act-btn rej" onClick={() => mDisarm.mutate(armed.id)} disabled={busy}>Désarmer</button>
                                    </div>
                                  );
                                }
                                if (armPopover[w.wallet]?.open) {
                                  const st = armPopover[w.wallet];
                                  return (
                                    <div className="popover" style={{ top: 0, left: 0 }}>
                                      <div className="popover-row">
                                        <span className="pop-lbl">size_mult</span>
                                        <input
                                          className="pop-in"
                                          type="number"
                                          placeholder="ex: 2.0"
                                          value={st.size_mult}
                                          onChange={(e) => patchArm(w.wallet, { size_mult: e.target.value })}
                                        />
                                      </div>
                                      <div className="popover-row">
                                        <span className="pop-lbl">cap SOL</span>
                                        <input
                                          className="pop-in"
                                          type="number"
                                          placeholder="ex: 0.5"
                                          value={st.size_cap_sol}
                                          onChange={(e) => patchArm(w.wallet, { size_cap_sol: e.target.value })}
                                        />
                                      </div>
                                      <div className="popover-row" style={{ gap: 6 }}>
                                        <button className="act-btn arm" onClick={() => doArm(w.wallet)} disabled={busy}>OK</button>
                                        <button className="act-btn" onClick={() => closeArm(w.wallet)}>Annuler</button>
                                      </div>
                                    </div>
                                  );
                                }
                                return (
                                  <div style={{ display: 'flex', gap: 4 }}>
                                    {/* tip_lamports non forwarded : bouton visible uniquement si non-armé → armedByAddr[w.wallet] = undefined */}
                                <button className="act-btn obs" onClick={() => mArm.mutate({ wallet: w.wallet, mode: 'observe' })} disabled={busy}>Observe</button>
                                    <button className="act-btn arm" onClick={() => openArm(w.wallet)} disabled={busy}>Armer</button>
                                  </div>
                                );
                              })()}
                            </td>
                            <td onClick={(e) => e.stopPropagation()}>
                              <button
                                className="sort-btn"
                                onClick={() => { setDrillBacktest(true); setDrillWallet(w.wallet); }}
                              >⏱ backtest</button>
                            </td>
                            <td onClick={(e) => e.stopPropagation()}>
                              <a
                                href={`https://solscan.io/account/${w.wallet}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ fontSize: 10, color: '#9bd1ff', marginRight: 6, textDecoration: 'none', fontWeight: 700 }}
                              >SS</a>
                              <a
                                href={`https://gmgn.ai/sol/address/${w.wallet}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ fontSize: 10, color: 'var(--amber)', textDecoration: 'none', fontWeight: 700 }}
                              >GM</a>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  </div>
                )}
              </div>

              {/* Trades shadow */}
              <div className="tbl-wrap">
                <div className="tbl-hd">
                  <span>Trades shadow (copytrade:would_buy)</span>
                  <span className="cnt">{shadowTotal} events · 200 affichés max · AUCUN achat réel</span>
                </div>
                {shadowTrades.length === 0 ? (
                  <div className="nodata" style={{ padding: 24 }}>
                    {shadowError ? 'Redis indisponible' : 'Aucun event — stream vide ou COPYTRADE_MODE=off'}
                  </div>
                ) : (
                  <>
                    <div className="shadow-note">
                      Mode SHADOW — aucun SOL engagé. Ces trades auraient ete achetes si COPYTRADE_MODE=on.
                    </div>
                    <table>
                      <thead>
                        <tr>
                          <th>Wallet</th>
                          <th>Mint</th>
                          <th>bc_pda</th>
                          <th className="num">SOL</th>
                          <th className="num">Prix</th>
                          <th className="num">Slot</th>
                          <th className="num">Heure</th>
                        </tr>
                      </thead>
                      <tbody>
                        {shadowTrades.map(tr => (
                          <tr key={tr.stream_id}>
                            <td><span className="addr" title={tr.wallet}>{fmtAddr(tr.wallet)}</span><CopyBtn text={tr.wallet} /></td>
                            <td><span className="addr" title={tr.mint}>{fmtAddr(tr.mint)}</span><CopyBtn text={tr.mint} /></td>
                            <td><span className="addr" title={tr.bc_pda}>{fmtAddr(tr.bc_pda)}</span><CopyBtn text={tr.bc_pda} /></td>
                            <td className="num">{tr.sol !== null ? tr.sol.toFixed(3) : <span className="muted">—</span>}</td>
                            <td className="num" style={{ fontSize: 10 }}>
                              {tr.price !== null ? tr.price.toExponential(4) : <span className="muted">—</span>}
                            </td>
                            <td className="num muted">{tr.slot || '—'}</td>
                            <td className="num">{fmtTs(tr.ts_observed)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </>
                )}
              </div>
            </>
          )}
          </>}
        </div>
      </div>
      {drillWallet && <TokenDrillPanel kind="wallet" id={drillWallet} openBacktest={drillBacktest} onClose={() => setDrillWallet(null)} />}
    </div>
  );
}

export default CopiedWalletsPage;
